import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';


const getFirstNonEmptyString = (...values) => values.find(
    (value) => typeof value === 'string' && value.trim().length > 0
);

const normalizeUserData = (user = {}, social = {}) => {
    const instagramFromSocial = social?.instagram || user?.social?.instagram || {};
    const resolvedProfilePictureUrl = getFirstNonEmptyString(
        user?.profilePictureUrl,
        user?.profileImage?.url,
        user?.instagramProfilePicture,
        instagramFromSocial?.profilePictureUrl,
        instagramFromSocial?.profileImage?.url
    );

    return {
        ...user,
        email: user.email || '',
        mobile: user.mobile || '',
        gender: user.gender || '',
        dob: user.dob || '',
        country: user.country || '',
        state: user.state || '',
        city: user.city || '',
        postalCode: user.postalCode || '',
        contentType: user.contentType || [],
        username: user.username || '',
        followersCount: user.instagramFollowers || 0,
        followsCount: user.instagramFollowing || 0,
        mediaCount: user.instagramMediaCount || 0,
        profilePictureUrl: resolvedProfilePictureUrl || '',
        instagramInsights: user.instagramInsights || [],
        name: user.name || '',
        profileImage: {
            ...user.profileImage,
            url: getFirstNonEmptyString(user?.profileImage?.url, resolvedProfilePictureUrl) || '',
        },
        // YouTube data
        youtubeConnected: !!social?.youtube?.connected,
        youtubeSubscribers: social?.youtube?.statistics?.subscriberCount || 0,
        youtubeVideoCount: social?.youtube?.statistics?.videoCount || 0,
        youtubeViewCount: social?.youtube?.statistics?.viewCount || 0,
        youtubeChannelTitle: social?.youtube?.title || '',
        youtubeThumbnail: social?.youtube?.thumbnail || '',
        hasSubscription: Boolean(user.hasSubscription),
        planType: user.planType || 'free',
        influencerSubscriptionId: user.influencerSubscriptionId || null,
    };
};

// Load initial state from localStorage
const loadInitialState = () => {
    try {
        const userData = localStorage.getItem('userData');
        const token = localStorage.getItem('token');
        const userType = localStorage.getItem('userType');
        return {
            data: userData ? JSON.parse(userData) : null,
            token: token || null,
            loading: false,
            fetchingUserDetails: false,
            error: null,
            userType: userType || null,
           
        };
    } catch (error) {
        console.error('Error loading state from localStorage:', error);
        return {
            data: null,
            token: null,
            loading: false,
            fetchingUserDetails: false,
            error: null,
            userType: null,
            
        };
    }
};
// Create async thunk for getting user profile
export const getUserProfile = createAsyncThunk(
    'user/getUserProfile',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }
            
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/users/profile`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            
            if (response.data.success && response.data.user) {
                const userData = normalizeUserData(response.data.user, response.data.social);
                localStorage.setItem('userData', JSON.stringify(userData));
                return userData;
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: 'Failed to fetch profile' });
        }
    }
);

// Add a new thunk for initial profile fetch
export const fetchInitialProfile = createAsyncThunk(
    'user/fetchInitialProfile',
    async (_, { dispatch }) => {
        const token = localStorage.getItem('token');
        if (token) {
            dispatch(getUserProfile());
        }
    }
);

// Async thunk to fetch contract status
export const fetchContractStatus = createAsyncThunk(
    "user/fetchContractStatus", 
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/auth/users/contract-status`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );
            return response.data.contractStatus;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch contract status");
        }
    }
);

const userSlice = createSlice({
    name: 'user',
    initialState: loadInitialState(),
    reducers: {
        saveUserDetails: (state, action) => {
            const normalizedPayload = normalizeUserData(action.payload);
            state.data = {
                ...state.data,
                ...normalizedPayload,
            };
            localStorage.setItem('userData', JSON.stringify(state.data));
            if (action.payload) {
                state.isAuthenticated = true;
            }
        },
        clearUserState: (state) => {
            state.data = null;
            state.token = null;
            state.loading = false;
            state.error = null;
            state.isAuthenticated = false;
            // Clear localStorage
            localStorage.removeItem('userData');
            localStorage.removeItem('token');
            // localStorage.removeItem('userType');
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
            state.loading = false;
        },
        clearError: (state) => {
            state.error = null;
        },
        setToken: (state, action) => {
            state.token = action.payload;
            state.isAuthenticated = !!action.payload;
            // Save token to localStorage
            if (action.payload) {
                localStorage.setItem('token', action.payload);
            }
        },
        clearUserDetails: (state) => {
            state.data = null;
            state.token = null;
            state.error = null;
            localStorage.removeItem('userType');
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getUserProfile.pending, (state) => {
                state.fetchingUserDetails = true;
                state.error = null;
            })
            .addCase(getUserProfile.fulfilled, (state, action) => {
                state.fetchingUserDetails = false;
                state.data = action.payload;
                state.error = null;
                localStorage.setItem('userType', 'influancer'); // ✅ Set userType when profile is fetched
                state.isAuthenticated = true;
            })
            .addCase(getUserProfile.rejected, (state, action) => {
                state.fetchingUserDetails = false;
                state.error = action.payload;
                localStorage.removeItem('userType'); // ✅ Remove userType on error
                state.isAuthenticated = false;
            })
            .addCase(fetchContractStatus.pending, (state) => {
                state.status = "loading";
              })
              .addCase(fetchContractStatus.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.contractStatus = action.payload;
              })
              .addCase(fetchContractStatus.rejected, (state) => {
                state.status = "failed";
              });
    }
});

export const { saveUserDetails, clearUserState, setLoading, setError, setToken ,clearUserDetails, clearError } = userSlice.actions;

export const selectInfluencerPlanType = (state) => state.user.data?.planType || 'free';
export const selectHasInfluencerSubscription = (state) => Boolean(state.user.data?.hasSubscription);

export default userSlice.reducer;
